import React from 'react';
import { useIntl } from 'react-intl';
import type { ArtistProfileResponse } from 'fym-shared/src/music24/artistProfile';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../components/primitives/SMLButton';
import { profileMessages as m } from '../profileMessages';
import { profileDownloadUrl } from '../profileAssets';
import { ProfileDownloadLink } from '../page/ProfileDownloadLink';
import { HAND_SERVICE_FACTS, HAND_SERVICES } from './handServices';
import { sendAllMessages as s } from './sendAllMessages';
import { useHandMarks, type HandRow } from './useHandMarks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Send to all sheet; each part uses its own slice of it */
import styles from './SendAll.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface HandRowViewProps {
  row: HandRow;
  disabled: boolean;
  onToggle: (row: HandRow) => void;
}

/** One service of "Set these by hand" (design c `.hr`): Download, the settings page, "I set it". */
const HandRowView: React.FC<HandRowViewProps> = ({ row, disabled, onToggle }) => {
  const intl = useIntl();
  const facts = HAND_SERVICE_FACTS[row.service];
  const download = profileDownloadUrl(row.downloadAsset);
  const testId = `send-all-hand-${row.service}`;
  return (
    <div className={styles.handRow} data-testid={testId}>
      <span className={styles.handLogo} data-service={row.service} aria-hidden="true">
        {facts.logo}
      </span>
      <div className={styles.handText}>
        <span className={styles.serviceName}>{facts.name}</span>
        <span className={styles.itemSub}>{intl.formatMessage(facts.takes)}</span>
      </div>
      <div className={styles.handActions}>
        {download !== null && <ProfileDownloadLink href={download} testId={`${testId}-download`} />}
        <a className={styles.link} href={facts.settingsUrl} target="_blank" rel="noopener noreferrer">
          {intl.formatMessage(s.openSettings)}
          <span aria-hidden="true">{' \u2197'}</span>
        </a>
        {row.setAt === null ? (
          <SMLButton className={styles.sm} disabled={disabled} onClick={() => onToggle(row)}>
            {intl.formatMessage(m.iSetIt)}
          </SMLButton>
        ) : (
          <>
            <span className={`${styles.chip} ${styles.chipOk}`} data-testid={`${testId}-set`}>
              {intl.formatMessage(s.setOn, { date: intl.formatDate(row.setAt, { day: 'numeric', month: 'short' }) })}
            </span>
            <SMLButton className={styles.sm} disabled={disabled} onClick={() => onToggle(row)}>
              {intl.formatMessage(s.undoSet)}
            </SMLButton>
          </>
        )}
      </div>
    </div>
  );
};

/** "Set these by hand" (design c2): the profiles that Music24 cannot reach. */
const SendAllHandListComponent: React.FC<{ accountId: number; profile: ArtistProfileResponse }> = ({
  accountId,
  profile,
}) => {
  const intl = useIntl();
  const marks = useHandMarks(accountId, profile);
  const sub =
    marks.setCount === 0
      ? intl.formatMessage(s.byHandNone)
      : intl.formatMessage(s.byHandCount, { set: marks.setCount, total: HAND_SERVICES.length });

  return (
    <>
      <div className={styles.lead}>
        <span className={styles.leadTitle}>{intl.formatMessage(s.byHand)}</span>
        <span className={styles.itemSub} data-testid="send-all-hand-count">
          {sub}
        </span>
      </div>
      <div className={styles.handList}>
        {marks.rows.map((row) => (
          <HandRowView
            key={row.service}
            row={row}
            disabled={marks.isMarking || !marks.hasPhoto}
            onToggle={marks.toggle}
          />
        ))}
      </div>
    </>
  );
};

export const SendAllHandList = TypedMemo(SendAllHandListComponent);
